import '../styles/tailwind.css'
import * as React from 'react'
import OauthCard from './OauthCard'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

interface IHiddenFields {
  client_id: string
  redirect_uri: string
  state?: string
  response_type: string
  response_mode?: string
  scope?: string
  code_challenge?: string
  code_challenge_method?: string
}

interface IScope {
  key: string
  title: string
  write: boolean
}

interface IProps {
  clientName: string
  appName: string
  redirectHost: string
  accountName?: string
  accountEmail?: string
  scopes: IScope[]
  authorizeUrl: string
  hiddenFields: IHiddenFields
  authenticityToken: string
  authorizeLabel: string
  denyLabel: string
}

const HiddenFields = ({ fields, authenticityToken }: { fields: IHiddenFields; authenticityToken: string }) => (
  <>
    {Object.entries(fields).map(([name, value]) => (
      value == null ? null : <input key={name} type="hidden" name={name} value={value} />
    ))}
    <input type="hidden" name="authenticity_token" value={authenticityToken} />
  </>
)

const AuthorizeApplication = ({
  clientName,
  appName,
  redirectHost,
  accountName,
  accountEmail,
  scopes,
  authorizeUrl,
  hiddenFields,
  authenticityToken,
  authorizeLabel,
  denyLabel,
}: IProps) => {
  const avatarFallback = (accountName || accountEmail || appName || 'B').charAt(0).toUpperCase()

  return (
    <OauthCard
      eyebrow="Authorization request"
      title={<>Authorize <strong>{clientName}</strong> to use your account?</>}
      description={`Review the access this application is requesting before connecting it to your ${appName} account.`}
      footer={
        <>
          <div className="grid grid-cols-2 gap-3">
            <form action={authorizeUrl} method="post">
              <HiddenFields fields={hiddenFields} authenticityToken={authenticityToken} />
              <Button type="submit" className="w-full" size="lg">
                {authorizeLabel}
              </Button>
            </form>
            <form action={authorizeUrl} method="post">
              <input type="hidden" name="_method" value="delete" />
              <HiddenFields fields={hiddenFields} authenticityToken={authenticityToken} />
              <Button type="submit" variant="outline" className="w-full" size="lg">
                {denyLabel}
              </Button>
            </form>
          </div>
          <p className="text-center text-xs text-muted-foreground">
            Authorizing will redirect you to <strong className="text-foreground">{redirectHost}</strong>.
          </p>
        </>
      }
    >
      <div className="flex items-center gap-3 rounded-lg border p-3">
        <Avatar size="lg">
          <AvatarFallback className="bg-primary text-primary-foreground">{avatarFallback}</AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          {accountName && <p className="truncate text-sm font-medium">{accountName}</p>}
          <p className="truncate text-sm text-muted-foreground">{accountEmail}</p>
        </div>
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between gap-2">
          <p className="text-sm font-semibold">This application will be able to:</p>
          <Badge variant="secondary">{scopes.length} permission{scopes.length === 1 ? '' : 's'}</Badge>
        </div>
        {scopes.length > 0 ? (
          <ul className="divide-y rounded-lg border">
            {scopes.map((scope) => (
              <li key={scope.key} className="flex items-center gap-3 p-3">
                <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-xs font-bold text-primary">
                  {scope.write ? 'W' : 'R'}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-medium">{scope.title}</p>
                  <p className="text-xs text-muted-foreground">{scope.key}</p>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="rounded-lg bg-muted p-4 text-sm text-muted-foreground">
            This application did not request any additional permissions.
          </p>
        )}
      </div>
    </OauthCard>
  )
}

export default AuthorizeApplication
